import upperFirst from 'lodash/upperFirst';

import { ErrorType } from '@ndvr/auth-api';

export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again later.';

const UNSAFE_ERROR_TYPES: ReadonlySet<string> = new Set([ErrorType.EX_GENERIC, ErrorType.EX_UNKNOWN_CLIENT]);
const KNOWN_ERROR_TYPES: ReadonlySet<string> = new Set(Object.values(ErrorType));

/**
 * True when the errorType marks a deliberate business error whose message
 * is written for end users. EX_GENERIC / EX_UNKNOWN_CLIENT (and anything
 * not in the ErrorType enum) may carry raw exception details.
 */
export const isUserSafeErrorType = (errorType: string | null | undefined): boolean =>
    !!errorType && KNOWN_ERROR_TYPES.has(errorType) && !UNSAFE_ERROR_TYPES.has(errorType);

/**
 * Returns the user-displayable message for a backend error response.
 * Backend text is passed through only for deliberate business error types;
 * everything else is replaced with GENERIC_ERROR_MESSAGE so exception
 * details never reach the UI.
 */
export const sanitizeBackendMessage = (
    backendMessage: string | null | undefined,
    errorType: string | null | undefined,
): string => (isUserSafeErrorType(errorType) && backendMessage ? backendMessage : GENERIC_ERROR_MESSAGE);

/**
 * Returns a human readable format of an error type
 * If the error type is generic, we return a generic message
 * If the error type start with EX_, we remove that before converting
 * Otherwise we just convert it to a human readable form
 *
 * @param errorType Type of the error thrown
 * @returns Human readable version of the error
 */
export const convertErrorType = (errorType?: ErrorType | null | 'NETWORK_ERROR' | 'EDGE_REJECTED') => {
    if (!errorType) {
        return 'Unknown Error';
    } else if (errorType === 'NETWORK_ERROR') {
        return 'Network Error';
    } else if (errorType === 'EDGE_REJECTED') {
        return "File couldn't be uploaded";
    } else if (errorType === ErrorType.EX_GENERIC) {
        return 'Server Error';
    } else {
        return upperFirst(
            (errorType.startsWith('EX_') ? errorType.substring(3) : errorType).toLowerCase().replaceAll('_', ' '),
        );
    }
};
